import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import { messageFromError } from "../../../shared/api/drfErrors";

import { isRequestAbortError } from "../../deals/api/deals";
import { getDealStatusLabel } from "../../deals/lib/dealStatusLabels";
import { formatCreatedDateUk } from "../../deals/lib/dealFormatting";
import {
  fetchPropertyDeals,
  pickCompactPropertyDealFields,
  unwrapPropertyDealsPage,
} from "../api/propertyDeals";

/**
 * D2-E1 — read-only party-filtered Deals on Property detail.
 * Shows only compact rows returned by the reverse endpoint.
 * Empty / non-party → omit section (never imply foreign Deals exist).
 *
 * D2-E1-R1 — abort in-flight load-more on property change / retry / unmount
 * in addition to generation + mounted stale-response guards.
 *
 * DEALS-VISUAL-QA-F1 — private owner/party work surface (not event timeline).
 */

function PropertyDealCard({ deal }) {
  const title = deal.property_title_snapshot || `Угода #${deal.id}`;
  return (
    <li
      className="property-deals-workspace__item"
      data-testid={`property-deal-row-${deal.id}`}
    >
      <Link
        to={`/deals/${deal.id}`}
        className="property-deals-workspace__card"
      >
        <div className="property-deals-workspace__card-main">
          <span className="property-deals-workspace__card-title">{title}</span>
          <span
            className={[
              "feature-deals-list__status-badge",
              `feature-deals-list__status-badge--${deal.status || "unknown"}`,
            ].join(" ")}
          >
            {getDealStatusLabel(deal.status)}
          </span>
        </div>
        <div className="property-deals-workspace__card-meta">
          {deal.created_at ? (
            <span className="feature-deals-list__cell-numeric">
              створено{" "}
              <time dateTime={deal.created_at}>
                {formatCreatedDateUk(deal.created_at)}
              </time>
            </span>
          ) : null}
          {deal.updated_at ? (
            <span className="feature-deals-list__cell-numeric">
              оновлено{" "}
              <time dateTime={deal.updated_at}>
                {formatCreatedDateUk(deal.updated_at)}
              </time>
            </span>
          ) : null}
        </div>
        <span className="property-deals-workspace__card-affordance" aria-hidden="true">
          <i className="fa-solid fa-chevron-right" />
        </span>
      </Link>
      {/* Explicitly never render financial / client / party fields */}
    </li>
  );
}

function PropertyDealsShell({
  children,
  labelledBy,
  ariaLabel,
  testId,
}) {
  return (
    <section
      className="property-deals-workspace"
      aria-labelledby={labelledBy}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <div className="container">
        <div className="row detail-page-align-row">
          <div className="property-deals-workspace__surface">
          <header className="property-deals-workspace__header">
            <div className="property-deals-workspace__heading-row">
              <span
                className="property-deals-workspace__privacy-icon"
                aria-hidden="true"
              >
                <i className="fa-solid fa-lock" />
              </span>
              <div>
                <p className="property-deals-workspace__eyebrow">
                  Моя робота з об&apos;єктом
                </p>
                <h2
                  id={labelledBy || undefined}
                  className="property-deals-workspace__title section-title"
                >
                  Ваші угоди
                </h2>
              </div>
            </div>
            <p className="property-deals-workspace__privacy-note">
              Цей блок бачите лише ви як увійдений користувач.
            </p>
          </header>
          {children}
          </div>
        </div>
      </div>
    </section>
  );
}

function PropertyDealsSection({ propertyId }) {
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const [deals, setDeals] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);

  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const loadMoreAbortRef = useRef(null);

  const abortLoadMore = () => {
    const active = loadMoreAbortRef.current;
    if (active) {
      loadMoreAbortRef.current = null;
      active.abort();
    }
    loadingMoreRef.current = false;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortLoadMore();
    };
  }, []);

  useEffect(() => {
    // New initial generation supersedes any in-flight load-more.
    abortLoadMore();
    setLoadingMore(false);

    if (propertyId == null || propertyId === "") {
      setLoadState("idle");
      setDeals([]);
      setNextPage(null);
      setPage(1);
      setLoadError(null);
      setLoadMoreError(null);
      return undefined;
    }

    const controller = new AbortController();
    const generation = ++requestGenerationRef.current;
    setLoadMoreError(null);
    setLoadState("loading");
    setLoadError(null);

    fetchPropertyDeals(propertyId, { page: 1 }, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (generation !== requestGenerationRef.current) return;
        if (!mountedRef.current) return;
        const pageData = unwrapPropertyDealsPage(data);
        const rows = pageData.results
          .map(pickCompactPropertyDealFields)
          .filter((row) => row && row.id != null);
        setDeals(rows);
        setNextPage(pageData.next);
        setPage(1);
        setLoadState("loaded");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (generation !== requestGenerationRef.current) return;
        if (!mountedRef.current) return;
        setDeals([]);
        setNextPage(null);
        setPage(1);
        setLoadError(messageFromError(error));
        setLoadState("error");
      });

    return () => {
      controller.abort();
      abortLoadMore();
    };
  }, [propertyId, retryToken]);

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
  };

  const handleLoadMore = () => {
    if (
      propertyId == null
      || propertyId === ""
      || !nextPage
      || loadingMoreRef.current
      || loadState !== "loaded"
    ) {
      return;
    }

    const controller = new AbortController();
    loadMoreAbortRef.current = controller;
    const generation = requestGenerationRef.current;
    const nextPageNum = page + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);

    fetchPropertyDeals(
      propertyId,
      { page: nextPageNum },
      { signal: controller.signal },
    )
      .then((data) => {
        if (controller.signal.aborted) return;
        if (generation !== requestGenerationRef.current) return;
        if (!mountedRef.current) return;
        const pageData = unwrapPropertyDealsPage(data);
        const rows = pageData.results
          .map(pickCompactPropertyDealFields)
          .filter((row) => row && row.id != null);
        setDeals((prev) => {
          const seen = new Set();
          const merged = [];
          for (const row of prev) {
            if (!row || row.id == null || seen.has(row.id)) continue;
            seen.add(row.id);
            merged.push(row);
          }
          for (const row of rows) {
            if (seen.has(row.id)) continue;
            seen.add(row.id);
            merged.push(row);
          }
          return merged;
        });
        setNextPage(pageData.next);
        setPage(nextPageNum);
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (generation !== requestGenerationRef.current) return;
        if (!mountedRef.current) return;
        setLoadMoreError(messageFromError(error));
      })
      .finally(() => {
        // Only the controller that still owns the ref may clear load-more state.
        // Obsolete / aborted requests must not reset a newer request's loading flag.
        if (loadMoreAbortRef.current !== controller) {
          return;
        }
        loadMoreAbortRef.current = null;
        if (generation !== requestGenerationRef.current || !mountedRef.current) {
          return;
        }
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  };

  if (propertyId == null || propertyId === "") {
    return null;
  }

  if (loadState === "idle") {
    return null;
  }

  if (loadState === "loading") {
    return (
      <PropertyDealsShell
        ariaLabel="Ваші угоди"
        testId="property-deals-section-loading"
      >
        <Spinner size="md" label="Завантаження ваших угод" />
      </PropertyDealsShell>
    );
  }

  if (loadState === "error") {
    return (
      <PropertyDealsShell
        ariaLabel="Ваші угоди"
        testId="property-deals-section-error"
      >
        <div role="alert">
          <p>{loadError || "Не вдалося завантажити ваші угоди."}</p>
          <button type="button" className="action-btn" onClick={handleRetry}>
            Спробувати ще
          </button>
        </div>
      </PropertyDealsShell>
    );
  }

  if (loadState === "loaded" && deals.length === 0) {
    return null;
  }

  return (
    <PropertyDealsShell
      labelledBy="property-deals-heading"
      testId="property-deals-section"
    >
      <ul
        className="property-deals-workspace__list"
        data-testid="property-deals-list"
      >
        {deals.map((deal) => (
          <PropertyDealCard key={deal.id} deal={deal} />
        ))}
      </ul>

      {loadMoreError ? (
        <div role="alert">
          <p>{loadMoreError}</p>
          <button
            type="button"
            className="action-btn"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            Спробувати ще
          </button>
        </div>
      ) : null}

      {nextPage ? (
        <button
          type="button"
          className="action-btn"
          onClick={handleLoadMore}
          disabled={loadingMore}
          data-testid="property-deals-load-more"
        >
          {loadingMore ? "Завантаження…" : "Показати ще"}
        </button>
      ) : null}
    </PropertyDealsShell>
  );
}

export default PropertyDealsSection;
